import styled from 'styled-components';

const FooterMain = styled.footer`
  background-color: ${({ theme }) => theme.colors.brandDark};
  color: ${({ theme }) => theme.colors.brandLight};

  ${({ simple }) =>
    simple &&
    `
    .footer-bottom {
      padding: 20px 32px;
    }
  `}

  .footer-inner {
    max-width: 1400px;
    margin: 0 auto;
    padding: 30px 32px 30px;

    @media (max-width: 859px) {
      padding: 48px 16px 32px;
    }
  }

  /* ── TOP SECTION ───────────────────────────────────────── */

  .footer-top {
    display: grid;
    gap: 48px 32px;
    grid-template-columns: 1.4fr 1fr 1fr 1fr;
    margin-bottom: 24px;

    @media (max-width: 1023px) {
      grid-template-columns: 1fr 1fr;
    }

    @media (max-width: 599px) {
      grid-template-columns: 1fr;
      gap: 36px;
    }
  }

  .footer-col--brand {
    @media (max-width: 1023px) {
      grid-column: 1 / -1;
    }
  }

  a:has(.footer-brand__name) {
    text-decoration: none;
  }

  .footer-brand__name {
    color: ${({ theme }) => theme.colors.highlight};
    font-size: 1.5rem;
    font-weight: 700;
    letter-spacing: 3px;
    margin-bottom: 1rem;
  }

  .footer-brand__about {
    color: ${({ theme }) => theme.colors.brandLight};
    opacity: 0.72;
    font-size: 0.9375rem;
    line-height: 1.65;
    margin-bottom: 1.5rem;
    max-width: 340px;

    p {
      margin: 0;
    }

    b,
    strong {
      font-weight: 600;
      opacity: 1;
    }
  }

  .footer-brand__socials {
    display: flex;
    gap: 10px;
  }

  .footer-social-link {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border: 1px solid rgba(255, 255, 255, 0.22);
    border-radius: 50%;
    transition:
      border-color 0.2s,
      background-color 0.2s;

    svg {
      width: 18px;
      height: 18px;
      fill: ${({ theme }) => theme.colors.brandLight};
      transition: fill 0.2s;
    }

    &:hover {
      border-color: ${({ theme }) => theme.colors.highlight};

      svg {
        fill: ${({ theme }) => theme.colors.highlight};
      }
    }
  }

  /* ── COLUMN SHARED ─────────────────────────────────────── */

  .footer-col__heading {
    color: ${({ theme }) => theme.colors.highlight};
    font-size: 16px;
    font-weight: 700;
    letter-spacing: 1.8px;
    text-transform: uppercase;
    margin-bottom: 1.25rem;
  }

  .footer-col__list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 12px;

    li a {
      color: ${({ theme }) => theme.colors.brandLight};
      opacity: 0.72;
      text-decoration: none;
      font-size: 0.9375rem;
      transition: opacity 0.2s;

      &:hover {
        opacity: 1;
      }
    }
  }

  .footer-col__location {
    color: ${({ theme }) => theme.colors.brandLight};
    opacity: 0.72;
    font-size: 0.9375rem;
  }

  /* ── DELIVERY AREAS ────────────────────────────────────── */

  .footer-delivery-areas {
    padding: 28px 0;
  }

  .footer-delivery-areas__list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px 18px;
  }

  .footer-delivery-areas__item {
    position: relative;
    color: ${({ theme }) => theme.colors.brandLight};
    font-size: 0.9375rem;

    &:not(:last-child):not(:nth-last-child(2))::after {
      content: '·';
      position: absolute;
      right: -12px;
      top: 0;
      opacity: 0.5;
    }

    a {
      color: ${({ theme }) => theme.colors.brandLight};
      opacity: 0.72;
      text-decoration: none;
      transition: opacity 0.2s;

      &:hover {
        opacity: 1;
      }
    }
  }

  .footer-delivery-areas__item--all {
    margin-left: 20px;

    a {
      color: ${({ theme }) => theme.colors.highlight};
      opacity: 1;
      font-weight: 600;
    }
  }

  /* ── DIVIDER ───────────────────────────────────────────── */

  .footer-divider {
    border: none;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    margin: 0;
  }

  /* ── MID SECTION ───────────────────────────────────────── */

  .footer-mid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 48px;
    padding: 28px 0;

    @media (max-width: 599px) {
      grid-template-columns: 1fr;
      gap: 36px;
      padding: 36px 0;
    }
  }

  .footer-newsletter__copy {
    color: ${({ theme }) => theme.colors.brandLight};
    opacity: 0.72;
    font-size: 0.9375rem;
    margin-bottom: 1.5rem;
  }

  .footer-newsletter__form {
    display: flex;

    @media (max-width: 479px) {
      flex-direction: column;
      gap: 12px;
    }
  }

  .footer-newsletter__input {
    flex: 1;
    padding: 14px 16px;
    background: transparent;
    border: 1px solid rgba(255, 255, 255, 0.28);
    color: ${({ theme }) => theme.colors.brandLight};
    font-size: 0.9375rem;
    min-width: 0;

    &::placeholder {
      color: rgba(255, 255, 255, 0.42);
    }

    &:focus {
      outline: none;
      border-color: rgba(255, 255, 255, 0.55);
    }
  }

  .footer-newsletter__btn {
    padding: 14px 28px;
    background-color: ${({ theme }) => theme.colors.highlight};
    border: none;
    color: ${({ theme }) => theme.colors.brandDark};
    font-size: 0.9375rem;
    font-weight: 700;
    letter-spacing: 0.5px;
    cursor: pointer;
    white-space: nowrap;
    transition: opacity 0.2s;

    &:hover {
      opacity: 0.88;
    }
  }

  .footer-license__text {
    color: ${({ theme }) => theme.colors.brandLight};
    opacity: 0.6;
    font-size: 0.875rem;
    line-height: 1.7;
  }

  /* ── BOTTOM BAR ────────────────────────────────────────── */

  .footer-bottom {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    padding-top: 32px;

    @media (max-width: 599px) {
      flex-direction: column;
      align-items: flex-start;
    }
  }

  .footer-bottom__copy {
    color: ${({ theme }) => theme.colors.brandLight};
    opacity: 0.5;
    font-size: 0.875rem;
  }

  .footer-bottom__links {
    display: flex;
    gap: 24px;
    flex-wrap: wrap;

    a {
      color: ${({ theme }) => theme.colors.brandLight};
      opacity: 0.5;
      text-decoration: none;
      font-size: 0.875rem;
      transition: opacity 0.2s;

      &:hover {
        opacity: 1;
      }
    }
  }
`;

export default FooterMain;
